import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSocket } from '../hook/useSocket';
import { useSelector, useDispatch } from 'react-redux';
import { ChatInterface } from '../utils/interfaces';
import TextMessage from './TextMessage';
import ImageMessage from './ImageMessage';
import VideoMessage from './VideoMessage';
import TypingMessage from './TypingMessage';
import SystemMessage from './SystemMessage';
import DateSeparator from './DateSeparator';
import { warning } from '../utils/console';
import Dialogs from '../component/DialogComponents/Dialogs';
import classNames from 'classnames';
import QueueModal from './Modal/QueueModal';
import AnnouncementBanner from '../component/announcement/AccouncementBanner';
import IntentSuggestionModal from './Modal/IntentSuggestionModal';
import { CacheServiceContext } from '../service/CacheService';
import { prependChats } from '../redux/state/chat';
import { normalizeChatHistoryItems } from '../utils/chatHistoryMessageParser';
import {
    conversationSpansMultipleDays,
    getDayKey,
    getDaySeparatorLabel,
} from '../utils/chatDateLabel';
import { useTranslation } from 'react-i18next';
import QueueProcessingDialog from './Modal/QueueProcessingDialog';

interface Props {
    height: number;
    bannerHeight: number;
    isIOS: boolean;
    isKeyBoardOpen: boolean;
    keyBoardHeight: number;
}

const Content = (props: Props) => {
    const socket = useSocket();
    const dispatch = useDispatch();
    const cacheService = useContext(CacheServiceContext);

    const { t } = useTranslation();

    const [typing, setTyping] =
        useState<'bot' | 'agent' | 'user' | 'system' | 'null'>('null');

    // Pagination state - start at 0 so first load is page 1
    // Maximum 6 pages (120 items / 20 items per page)
    const MAX_PAGES = 6;
    const [currentPage, setCurrentPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    const [initialLoadRetryCount, setInitialLoadRetryCount] = useState(0);
    const MAX_RETRIES = 5;
    const RETRY_DELAY = 2000; // 2 seconds between retries (shorter delay)

    let isNewOwner: boolean = false;

    let currentOwner = 'null';

    const chats = useSelector((state: { chat: ChatInterface[] }) => state.chat);
    const showDaySeparators = useMemo(
        () => conversationSpansMultipleDays(chats),
        [chats]
    );
    const sessionId = useSelector(
        (state: { session: { sessionId: string } }) => state.session.sessionId
    );
    const isAuthenticated = useSelector(
        (state: { session: { isAuthenticated: boolean } }) => state.session.isAuthenticated
    );

    const chatHistoriesListStatus = useSelector(
        (state: { session: { chatHistoriesListStatus: boolean } }) => state.session.chatHistoriesListStatus
    );

    const chatId = useSelector(
        (state: { session: { chatId: string } }) => state.session.chatId
    );
    const conversationId = useSelector(
        (state: { session: { conversationId: string } }) => state.session.conversationId
    );
    const accessToken = useSelector(
        (state: { session: { accessToken: string } }) => state.session.accessToken
    );
    const conversationEntryId = useSelector(
        (state: { session: { conversationEntryId: string } }) => state.session.conversationEntryId
    );

    const isChatAlive = useSelector(
        (state: { status: { isChatAlive: Boolean } }) =>
            state.status.isChatAlive
    );

    const botChatBgColor = useSelector(
        (state: { settings: { botChatBgColor: string } }) =>
            state.settings.botChatBgColor
    );

    const bottomElement = useRef<null | HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const previousScrollHeightRef = useRef<number>(0);
    const lastScrollTopRef = useRef<number>(0);
    const scrollCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const scrollToBottom = () => {
        if (shouldScrollToBottom) {
            bottomElement.current?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const announcementIsEnabled = useSelector(
        (state: { settings: { announcementIsEnabled: boolean } }) =>
            state.settings.announcementIsEnabled
    );

    const isBotTyping = useSelector(
        (state: { status: { isBotTyping: Boolean } }) =>
            state.status.isBotTyping
    );

    const processor = useSelector(
        (state: { settings: { processor: number } }) =>
            state.settings.processor
    );

    // Load chat history when scrolling to top
    const loadChatHistory = async () => {
        // Only load if user is authenticated
        if (!isAuthenticated) {
            return;
        }
        
        if (isLoadingHistory || !hasMore || !sessionId || !cacheService?.getChatHistoriesList) {
            return;
        }

        // Check if we've reached maximum pages
        if (currentPage >= MAX_PAGES) {
            setHasMore(false);
            return;
        }

        setIsLoadingHistory(true);
        try {
            const nextPage = currentPage + 1; // First load: 0+1=1, second: 1+1=2, etc.
            
            // Don't exceed maximum pages
            if (nextPage > MAX_PAGES) {
                setHasMore(false);
                setIsLoadingHistory(false);
                return;
            }
            
            const response: any = await cacheService.getChatHistoriesList(sessionId, nextPage, 20);
            
            if (response && response.success) {
                // Check if there are items to load
                if (response.items && response.items.length > 0) {
                    const normalizedItems = normalizeChatHistoryItems(response.items);
                    
                    // Reverse items so oldest of this page appears first when prepended
                    const reversedItems = [...normalizedItems].reverse();
                    
                    // Store scroll position before prepending (only if not initial load)
                    const scrollContainer = scrollContainerRef.current;
                    if (scrollContainer && currentPage > 0) {
                        previousScrollHeightRef.current = scrollContainer.scrollHeight;
                    }

                    // Prepend older chats (oldest first)
                    dispatch(prependChats(reversedItems));
                    setCurrentPage(nextPage);
                    
                    // Stop if we've reached max pages or if API says no more pages
                    if (nextPage >= MAX_PAGES || nextPage >= response.totalPages || response.totalPages === 0) {
                        setHasMore(false);
                    }
                } else {
                    // If any page has no content, stop loading immediately
                    console.log(`Page ${nextPage} has no content - stopping load`);
                    setHasMore(false);
                }
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error loading chat history:', error);
            setHasMore(false);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    // Reset pagination state when authenticated (but don't auto-load - only on scroll-up)
    useEffect(() => {
        if (isAuthenticated && sessionId) {
            // If we haven't loaded yet, or if we're at page 0 with no more, reset to allow loading
            if (!initialLoadDone || (currentPage === 0 && !hasMore)) {
                console.log('Resetting pagination state after authentication (scroll-up only, no auto-load)');
                setHasMore(true);
                setCurrentPage(0);
                setInitialLoadDone(false);
            }
        }
    }, [isAuthenticated, sessionId]);

    // This function handles loading page 1 with retry logic
    const loadPage1WithRetry = useRef<((retryAttempt: number) => Promise<void>) | null>(null);
    const retryTimerRef = useRef<NodeJS.Timeout | null>(null);
    const page1LoadTimerRef = useRef<NodeJS.Timeout | null>(null);
    const isPage1LoadingRef = useRef<boolean>(false);
    
    // Initialize the load page 1 function
    useEffect(() => {
        if (isAuthenticated && sessionId && cacheService?.getChatHistoriesList && chatHistoriesListStatus !== undefined) {
            loadPage1WithRetry.current = async (retryAttempt: number = 0) => {
                console.log(`Loading page 1 - Attempt ${retryAttempt + 1}/${MAX_RETRIES + 1}`);
                setIsLoadingHistory(true);
                
                try {
                    const page1 = 1; // Load page 1 first
                    const response: any = await cacheService.getChatHistoriesList(sessionId, page1, 20);
                    
                    if (response && response.success) {
                        // Check if there are items to load
                        if (response.items && response.items.length > 0) {
                            const normalizedItems = normalizeChatHistoryItems(response.items);
                            
                            // Reverse items so oldest of this page appears first when prepended
                            const reversedItems = [...normalizedItems].reverse();
                            
                            // Prepend older chats (page 1 - oldest first)
                            console.log(`Successfully loaded ${reversedItems.length} items on attempt ${retryAttempt + 1}`);
                            dispatch(prependChats(reversedItems));
                            setCurrentPage(page1);
                            
                            // Stop if page 1 is the last page, or if we've reached max pages
                            if (page1 >= MAX_PAGES || page1 >= response.totalPages || response.totalPages === 0) {
                                setHasMore(false);
                            }
                            
                            // Success - reset retry count and mark as done
                            setInitialLoadRetryCount(0);
                            setIsLoadingHistory(false);
                            setInitialLoadDone(true);
                            
                            // Note: MessageRead will be sent via IntersectionObserver in TextMessage component
                            // when the message with conversationEntryId becomes visible
                            
                            return; // Exit on success
                        } else {
                            // If page 1 has no content, stop loading immediately (not an error, just no data)
                            console.log('Page 1 has no content - no chat history available');
                            setHasMore(false);
                            setCurrentPage(0);
                            setInitialLoadRetryCount(0);
                            setIsLoadingHistory(false);
                            setInitialLoadDone(true);
                            return; // Exit - no data is not an error
                        }
                    } else {
                        // API returned unsuccessful response - retry if we haven't exceeded max retries
                        throw new Error('API returned unsuccessful response');
                    }
                } catch (error) {
                    console.error(`Error loading page 1 (attempt ${retryAttempt + 1}/${MAX_RETRIES + 1}):`, error);
                    
                    // Check if we should retry
                    if (retryAttempt < MAX_RETRIES) {
                        const nextRetry = retryAttempt + 1;
                        setInitialLoadRetryCount(nextRetry);
                        setIsLoadingHistory(false);
                        
                        // Retry after delay (2 seconds)
                        console.log(`Will retry in ${RETRY_DELAY / 1000} seconds... (attempt ${nextRetry + 1}/${MAX_RETRIES + 1})`);
                        retryTimerRef.current = setTimeout(() => {
                            if (isAuthenticated && sessionId && !initialLoadDone && hasMore && cacheService?.getChatHistoriesList && loadPage1WithRetry.current) {
                                console.log(`Retrying page 1 load (attempt ${nextRetry + 1}/${MAX_RETRIES + 1})...`);
                                loadPage1WithRetry.current(nextRetry);
                            }
                        }, RETRY_DELAY);
                    } else {
                        // Max retries exceeded - give up
                        console.error('Max retries exceeded for page 1 load');
                        setHasMore(false);
                        setInitialLoadRetryCount(0);
                        setIsLoadingHistory(false);
                        setInitialLoadDone(true);
                    }
                }
            };
        }
        
        // Cleanup timers on unmount
        return () => {
            if (retryTimerRef.current) {
                clearTimeout(retryTimerRef.current);
            }
            if (page1LoadTimerRef.current) {
                clearTimeout(page1LoadTimerRef.current);
            }
        };
    }, [isAuthenticated, sessionId, cacheService, dispatch, hasMore, initialLoadDone, chatHistoriesListStatus]);

    // Auto-load page 1 when chatHistoriesListStatus becomes true (on event or refresh)
    useEffect(() => {
        if (
            chatHistoriesListStatus && 
            isAuthenticated && 
            sessionId && 
            cacheService?.getChatHistoriesList && 
            !initialLoadDone && 
            currentPage === 0 && 
            hasMore && 
            !isLoadingHistory &&
            !isPage1LoadingRef.current &&
            loadPage1WithRetry.current
        ) {
            console.log('Auto-loading page 1 - chatHistoriesListStatus is true');
            isPage1LoadingRef.current = true;
            loadPage1WithRetry.current(0);
            isPage1LoadingRef.current = false;
        }
    }, [chatHistoriesListStatus, isAuthenticated, sessionId, initialLoadDone, currentPage, hasMore, isLoadingHistory, cacheService]);

    // Restore scroll position after chats are updated (when loading history)
    useEffect(() => {
        if (isLoadingHistory === false && previousScrollHeightRef.current > 0 && currentPage > 1) {
            const scrollContainer = scrollContainerRef.current;
            if (scrollContainer) {
                const newScrollHeight = scrollContainer.scrollHeight;
                const scrollDifference = newScrollHeight - previousScrollHeightRef.current;
                scrollContainer.scrollTop = scrollDifference;
                previousScrollHeightRef.current = 0; // Reset
            }
        }
    }, [chats, isLoadingHistory, currentPage]);

    // Check scroll position periodically to detect scroll-up (without using onScroll handler)
    useEffect(() => {
        if (!isAuthenticated) {
            // Clear interval if not authenticated
            if (scrollCheckIntervalRef.current) {
                clearInterval(scrollCheckIntervalRef.current);
                scrollCheckIntervalRef.current = null;
            }
            return;
        }

        // Initialize last scroll position
        if (scrollContainerRef.current) {
            lastScrollTopRef.current = scrollContainerRef.current.scrollTop;
        }

        // Track wheel events (desktop) and touch events (mobile) to detect scroll attempts even when content doesn't overflow
        let scrollDirection = 0; // -1 = up, 1 = down, 0 = none
        let touchStartY = 0;
        let touchStartTime = 0;
        
        const handleWheel = (e: WheelEvent) => {
            // Track wheel direction (positive = scroll down, negative = scroll up)
            scrollDirection = e.deltaY > 0 ? 1 : (e.deltaY < 0 ? -1 : 0);
        };
        
        const handleTouchStart = (e: TouchEvent) => {
            // Track initial touch position and time
            if (e.touches.length > 0) {
                touchStartY = e.touches[0].clientY;
                touchStartTime = Date.now();
            }
        };
        
        const handleTouchMove = (e: TouchEvent) => {
            // Track touch movement direction
            if (e.touches.length > 0 && touchStartY > 0) {
                const currentY = e.touches[0].clientY;
                const deltaY = currentY - touchStartY;
                
                // Only register if movement is significant (more than 10px)
                if (Math.abs(deltaY) > 10) {
                    // If moving up (currentY < touchStartY), user is trying to scroll up
                    scrollDirection = deltaY < 0 ? -1 : 1;
                }
            }
        };
        
        const handleTouchEnd = () => {
            // Reset touch tracking
            touchStartY = 0;
            touchStartTime = 0;
        };

        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            // Add wheel event for desktop
            scrollContainer.addEventListener('wheel', handleWheel, { passive: true });
            // Add touch events for mobile
            scrollContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
            scrollContainer.addEventListener('touchmove', handleTouchMove, { passive: true });
            scrollContainer.addEventListener('touchend', handleTouchEnd, { passive: true });
            scrollContainer.addEventListener('touchcancel', handleTouchEnd, { passive: true });
        }

        // Set up interval to check scroll position
        scrollCheckIntervalRef.current = setInterval(() => {
            const scrollContainer = scrollContainerRef.current;
            if (!scrollContainer) return;

            const scrollTop = scrollContainer.scrollTop;
            const scrollHeight = scrollContainer.scrollHeight;
            const clientHeight = scrollContainer.clientHeight;
            const isScrollable = scrollHeight > clientHeight;

            // Detect scroll-up: if scrollTop decreased (user scrolled up)
            const scrolledUp = scrollTop < lastScrollTopRef.current;
            lastScrollTopRef.current = scrollTop;

            // Only trigger getChatHistoriesList when actually at the top (scrollTop <= 10px) and scrolled up
            const isAtTop = scrollTop <= 10;
            
            // When content doesn't overflow but chatHistoriesListStatus is enabled,
            // detect scroll-up attempts via wheel events (desktop) or touch events (mobile) even when scrollTop is 0
            const isTryingToScrollUp = !isScrollable && chatHistoriesListStatus && isChatAlive && scrollDirection === -1;
            
            // Check if we should trigger loading
            // Either: (at top AND scrolled up) OR (trying to scroll up when content doesn't overflow)
            const shouldTrigger = ((isAtTop && scrolledUp) || isTryingToScrollUp) && hasMore && !isLoadingHistory;
            
            if (shouldTrigger) {
                setShouldScrollToBottom(false);
                
                if (currentPage === 0 && !initialLoadDone && !isPage1LoadingRef.current && loadPage1WithRetry.current) {
                    // Clear any existing timer
                    if (page1LoadTimerRef.current) {
                        clearTimeout(page1LoadTimerRef.current);
                    }
                    
                    isPage1LoadingRef.current = true;
                    
                    // If chatHistoriesListStatus is true, load immediately without delay
                    if (chatHistoriesListStatus) {
                        console.log('Scrolled to top - loading page 1 immediately (chatHistoriesListStatus enabled)');
                        if (isAuthenticated && sessionId && !initialLoadDone && hasMore && loadPage1WithRetry.current) {
                            loadPage1WithRetry.current(0);
                        }
                        isPage1LoadingRef.current = false;
                    } else {
                        // Otherwise, use 5 second delay
                        console.log('Scrolled to top - will load page 1 after 5 second delay (scroll-only, no auto-load on refresh)');
                        page1LoadTimerRef.current = setTimeout(() => {
                            if (isAuthenticated && sessionId && !initialLoadDone && hasMore && loadPage1WithRetry.current) {
                                loadPage1WithRetry.current(0);
                            }
                            isPage1LoadingRef.current = false;
                        }, 5000); // 5 second delay for first attempt
                    }
                } else if (currentPage > 0) {
                    // Load subsequent pages immediately when scrolled to top
                    console.log(`Scrolled to top - loading page ${currentPage + 1} (scroll-only)`);
                    loadChatHistory();
                }
            } else if (scrollTop > scrollHeight - clientHeight - 100) {
                // If scrolled near bottom, enable auto-scroll to bottom
                setShouldScrollToBottom(true);
            }
            
            // Reset scroll direction after checking (reset it so it doesn't trigger multiple times)
            if (scrollDirection !== 0) {
                scrollDirection = 0;
            }
        }, 100); // Check every 100ms

          // Cleanup interval and event listeners on unmount or when authentication changes
          return () => {
              if (scrollCheckIntervalRef.current) {
                  clearInterval(scrollCheckIntervalRef.current);
                  scrollCheckIntervalRef.current = null;
              }
              const container = scrollContainerRef.current;
              if (container) {
                  container.removeEventListener('wheel', handleWheel);
                  container.removeEventListener('touchstart', handleTouchStart);
                  container.removeEventListener('touchmove', handleTouchMove);
                  container.removeEventListener('touchend', handleTouchEnd);
                  container.removeEventListener('touchcancel', handleTouchEnd);
              }
          };
      }, [isAuthenticated, chatHistoriesListStatus, hasMore, isLoadingHistory, currentPage, initialLoadDone, sessionId, isChatAlive]);

    useEffect(() => {
        if (shouldScrollToBottom) {
            scrollToBottom();
        }
        // if (chats.length > 0) setTyping(chats[chats.length - 1].owner);
    }, [chats, shouldScrollToBottom]);

    // let [searchParams, setSearchParams] = useSearchParams();

    // const handleClick = () => {
    //     console.log(socket);
    //     var data = {
    //         eventType: 'connect',
    //         timestamp: '123',
    //         botId: '30',
    //         acctId: '5',
    //         dbProfile: 'mario.staging.1',
    //     };
    //     socket.send(JSON.stringify(data));
    // };

    return (
        <div
            // style={{backgroundColor: `${botChatBgColor?botChatBgColor.slice(0,7):''}`}}
            style={{
                backgroundColor: `${botChatBgColor?botChatBgColor.slice(0,7):''}`, 
                // height: (props.height - (isChatAlive?68:0) - props.bannerHeight - (announcementIsEnabled?30:0))
                ...(props.isIOS
                    ? (() => {
                          const base = props.isKeyBoardOpen
                              ? props.keyBoardHeight
                              : props.height;
                          const computed =
                              base -
                              (isChatAlive ? 68 : 0) -
                              props.bannerHeight -
                              (announcementIsEnabled ? 30 : 0);
                          // Prevent 0/negative heights (can look like "blank chat" on iOS)
                          return { height: Math.max(1, computed) };
                      })()
                    : {})
            }}
            // style={{backgroundColor: `${botChatBgColor?botChatBgColor.slice(0,7):''}`, height: (props.height - props.bannerHeight - (announcementIsEnabled?30:0))}}
            className={classNames('h-full w-full no-scrollbar relative duration-300', {
                'h-100pc rounded-b-xl': !isChatAlive,
                'bg-slate-200': !botChatBgColor,
                'overflow-y-scroll': isAuthenticated && chatHistoriesListStatus && isChatAlive,
                'overflow-y-auto': !(isAuthenticated && chatHistoriesListStatus && isChatAlive),
            })}
        >
            <div 
                ref={scrollContainerRef}
                className={classNames("h-full no-scrollbar", {
                    'pointer-events-none': !isChatAlive,
                    'overflow-y-scroll': isAuthenticated && chatHistoriesListStatus && isChatAlive,
                    'overflow-y-auto': !(isAuthenticated && chatHistoriesListStatus && isChatAlive),
                })}
            >
                {isLoadingHistory && (
                    <div className="flex justify-center items-center p-4">
                        <div className="text-sm text-gray-500">{t("loadingChatHistoriesList")}</div>
                    </div>
                )}
                {!hasMore && currentPage > 0 && !isLoadingHistory && (
                    <div className="flex justify-center items-center p-4">
                        <div className="text-sm text-gray-400">{t("noMoreChatHistoriesList")}</div>
                    </div>
                )}
                {chats.map((item: ChatInterface, index) => {
                    isNewOwner = currentOwner != item.owner;
                    currentOwner = item.owner;

                    const dayKey = getDayKey(item.createdBy);
                    const prevDayKey =
                        index > 0 ? getDayKey(chats[index - 1].createdBy) : null;
                    const dayLabel = showDaySeparators
                        ? getDaySeparatorLabel(item.createdBy)
                        : null;
                    // Multi-day only: separator before first message and on every day change.
                    const shouldRenderSeparator =
                        !!dayLabel &&
                        dayKey !== null &&
                        (index === 0 || dayKey !== prevDayKey);

                    const wrapWithSeparator = (node: React.ReactNode) => (
                        <React.Fragment key={index}>
                            {shouldRenderSeparator ? (
                                <DateSeparator label={dayLabel as string} />
                            ) : null}
                            {node}
                        </React.Fragment>
                    );

                    switch (item.owner) {
                        case 'bot':
                        case 'agent':
                        case 'user': {
                            switch (item.type) {
                                case 'text': {
                                    return wrapWithSeparator(
                                        <TextMessage
                                            owner={item.owner}
                                            name={item.name}
                                            message={item.message}
                                            isNewOwner={isNewOwner}
                                            time={item.displayTime}
                                            createdBy={item.createdBy}
                                            firstMessage={item.firstMessage}
                                            conversationEntryId={item.conversationEntryId}
                                            messageId={item.messageId}
                                        />
                                    );
                                }
                                case 'image': {
                                    return wrapWithSeparator(
                                        <ImageMessage
                                            owner={item.owner}
                                            name={item.name}
                                            message={item.message}
                                            isNewOwner={isNewOwner}
                                            time={item.displayTime}
                                            createdBy={item.createdBy}
                                        />
                                    );
                                }
                                case 'video': {
                                    return wrapWithSeparator(
                                        <VideoMessage
                                            owner={item.owner}
                                            name={item.name}
                                            message={item.message}
                                            isNewOwner={isNewOwner}
                                            time={item.displayTime}
                                            createdBy={item.createdBy}
                                        />
                                    );
                                }
                            }
                            break;
                        }
                        case 'system': {
                            return wrapWithSeparator(
                                <SystemMessage message={item.message.content} />
                            );
                        }
                        default: {
                            warning(
                                Content.name,
                                'Owner not in [bot, agent, user, system], got owner and message:',
                                item.owner,
                                item.message
                            );
                            break;
                        }
                    }
                    return null;
                })}
                {/* <SystemMessage message="Mario has joined the chat. Mario has joined the chat. Mario has joined the chat. Mario has joined the chat." /> */}
                {(processor === 2 || processor === 3) && (
                    <TypingMessage owner={isBotTyping ? 'bot' : 'null'} name="mario" isNewOwner={false} />
                )}
                {/* <TypingMessage owner={"null"} name="Mario" isNewOwner={false} /> */}
                <div ref={bottomElement} className="h-20" />
            </div>
            <div className={classNames({'absolute w-full h-full top-0 bg-slate-200/50 rounded-b-xl': !isChatAlive})}></div>
            <Dialogs />
            <QueueProcessingDialog/>
            {isChatAlive && <IntentSuggestionModal />}
        </div>
    );
};

Content.propTypes = {};

export default Content;
